import { Constants } from '../constants';

/**
 * Extracts a user-friendly message from any error shape encountered in the
 * project — HTTP responses, RxJS errors, plain Error objects, or raw strings.
 *
 * Order of preference (first non-empty wins):
 *   1. `err.error.data.message`  — the project's canonical backend envelope
 *   2. `err.error.data[*]`       — 422 validation envelope: [{field, message}]
 *   3. `err.error.errors[*]`     — array of validation errors (joined)
 *   4. `err.error.message`       — alternative backend shape
 *   5. `err.message`             — RxJS / runtime / Error.message
 *   6. raw string (when `err` itself is a string)
 *
 * Notably we DO NOT fall back to `err.statusText`. HTTP status text leaks raw
 * server-internal phrases like "Internal Server Error" / "Bad Gateway" that
 * are not appropriate to show end users. When nothing usable is found we
 * return the friendly `Constants.SOMETHING_WENT_WRONG` fallback.
 *
 * This is the single source of truth for "what message do we show the user
 * for this error?" — import it anywhere an alert/toast needs to surface a
 * backend or runtime error.
 */
/**
 * Angular's own `HttpErrorResponse.message`:
 *   `Http failure response for https://api.example/v1/thing: 0 Unknown Error`
 * Also matches the progress-event variant (`Http failure during parsing for …`).
 *
 * Exported so the display boundary can scrub a message that reached it as a bare
 * string — see `AlertService.error`. ~124 legacy call sites pass `error.message`
 * straight to a toast, and those bypass this util entirely.
 */
export const HTTP_FRAMEWORK_MESSAGE = /^Http failure (response|during parsing) for /i;

/** Any absolute URL embedded in text destined for a user. */
export const EMBEDDED_URL = /\bhttps?:\/\/\S+/i;

/**
 * The user-facing message for a failure that never produced a usable response.
 *
 * `navigator.onLine` is a HINT, not the truth: it reports whether a network
 * interface exists, so it is `true` on a captive-portal Wi-Fi with no route to
 * the internet. It is trusted in one direction only — when it says `false` the
 * device really is offline, so that message can be stated confidently. Online
 * plus no response stays deliberately non-committal.
 */
export function transportMessage(): string {
  const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  return online ? Constants.UNREACHABLE : Constants.NO_INTERNET;
}

/**
 * Map a status with no usable backend envelope onto the taxonomy.
 * Returns `null` when the status carries no class-wide message of its own, so
 * the caller falls through to its normal handling.
 */
function messageForStatus(status: number | undefined): string | null {
  if (status === 0 || status === undefined) return transportMessage();
  if (status === 408 || status === 504) return Constants.REQUEST_TIMEOUT;
  if (status >= 500) return Constants.SERVER_ERROR;
  return null;
}

/**
 * Scrub a string that is about to be shown to a user.
 *
 * Applied at the display boundary (`AlertService.error` / `.warning`) so it
 * catches messages that never went through `extractErrorMessage` — the ~124
 * call sites that toast `error.message` themselves. Idempotent: running it over
 * an already-clean message returns it unchanged, so it is safe to layer on top
 * of the interceptor rather than instead of it.
 */
export function sanitizeUserFacingError(message: string | null | undefined): string {
  const text = (message ?? '').trim();
  if (!text) return Constants.SOMETHING_WENT_WRONG;
  if (HTTP_FRAMEWORK_MESSAGE.test(text)) return transportMessage();
  // A URL in user-facing copy is either the framework naming our API host or a
  // stack frame. Neither is for the reader, and neither is worth guessing at.
  if (EMBEDDED_URL.test(text)) return Constants.SOMETHING_WENT_WRONG;
  return text;
}

export function extractErrorMessage(err: any): string {
  if (err == null) return Constants.SOMETHING_WENT_WRONG;

  // Raw string thrown / passed directly. Scrubbed, because a caller that has
  // already unwrapped `error.message` hands us the framework string.
  if (typeof err === 'string') {
    const trimmed = err.trim();
    if (!trimmed) return Constants.SOMETHING_WENT_WRONG;
    if (HTTP_FRAMEWORK_MESSAGE.test(trimmed)) return transportMessage();
    if (EMBEDDED_URL.test(trimmed)) return Constants.SOMETHING_WENT_WRONG;
    return trimmed;
  }

  /* A transport failure has no backend envelope to prefer — nothing answered —
     so it is classified BEFORE the candidate list. Reaching the list would have
     fallen through to `err.message`, which is the reported bug: the toast read
     `Http failure response for https://staging.api2…/gmail/signatures: 0 Unknown
     Error`. Note this also restores the notify stack's de-duplication: five
     concurrent failures used to produce five DIFFERENT strings (one URL each) and
     therefore five stacked toasts, where one shared string collapses to a single
     row with a count. */
  const isHttp = typeof err?.status === 'number' && ('error' in err || 'statusText' in err);
  if (isHttp && err.status === 0) return transportMessage();

  const candidates: unknown[] = [
    err?.error?.data?.message,
    Array.isArray(err?.error?.data) ? err.error.data.map((item: any) => item?.message).filter(Boolean).join(', ') : undefined,
    Array.isArray(err?.error?.errors) ? err.error.errors.filter(Boolean).join(', ') : undefined,
    err?.error?.message,
    err?.message,
  ];

  for (const c of candidates) {
    if (typeof c !== 'string' || !c.trim()) continue;
    const text = c.trim();
    /* The backend envelope (candidates 1–4) is a DELIBERATE, user-directed
       message and still wins — "Signature name is already in use" has to reach
       the reader, or a blanket "hide backend errors" rule leaves them stuck with
       nothing to fix. Only the framework's own string, and anything carrying an
       absolute URL, is refused: that is infrastructure detail, not copy. */
    if (HTTP_FRAMEWORK_MESSAGE.test(text)) break;
    if (EMBEDDED_URL.test(text)) break;
    return text;
  }

  // Nothing usable was authored for this failure — fall back on its class. Only
  // an HTTP failure HAS a class; a plain Error with no message gets the generic
  // fallback rather than being described as a network problem.
  const byStatus = isHttp ? messageForStatus(err.status) : null;
  return byStatus ?? Constants.SOMETHING_WENT_WRONG;
}
